import { useEffect } from 'react'
import { useCustomsAnswerView } from './customsAnswerViewStore'
import {
  customsAttentionCell,
  customsCertificateCell,
  customsDocsCell,
  customsDutyCell,
  customsFeaturesCell,
  customsFeeCell,
  customsStatusLabel,
  customsTnvedCell,
  customsZnakCell,
  formatCustomsDate,
  type CustomsCellValue,
} from './itemColDefs.customs.cells'

/**
 * Полный ответ брокера по позиции для продаж (с 30.10.2026, просьба логиста):
 * в таблице длинные тексты «Нетарифка», «Сертификат», «Особенности» — в одну
 * строку, здесь — целиком, как их прислал брокер.
 */
export function CustomsAnswerViewModal() {
  const open = useCustomsAnswerView((s) => s.open)
  const close = useCustomsAnswerView((s) => s.close)

  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [open, close])

  if (!open) return null
  const { customs, partNumber } = open
  const check = customs.check
  const rows: Array<[string, CustomsCellValue | null]> = [
    ['ТН ВЭД', customsTnvedCell(customs)],
    ['Пошлина', customsDutyCell(customs)],
    ['Сбор', customsFeeCell(customs)],
    ['Честный знак', customsZnakCell(customs)],
    ['Нетарифка', customsDocsCell(customs)],
    ['Сертификат', customsCertificateCell(customs)],
    ['Особенности', customsFeaturesCell(customs)],
    ['Внимание', customsAttentionCell(customs)],
  ]
  const status = customsStatusLabel(customs)
  const checkedAt = formatCustomsDate(check?.checkedAt)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) close()
      }}
    >
      <div
        role="dialog"
        aria-label={`Ответ брокера: ${partNumber}`}
        className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col"
      >
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-800 truncate">
              Нетарифка: {partNumber}
            </h3>
            <p className="text-xs text-gray-500">
              {[status, checkedAt && `ответ от ${checkedAt}`, check?.brokerRequest?.fileCode]
                .filter(Boolean)
                .join(' · ') || 'Позицию логисту не отправляли'}
            </p>
          </div>
          <button
            onClick={close}
            aria-label="Закрыть"
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            ×
          </button>
        </div>
        <dl className="p-4 overflow-y-auto space-y-3">
          {rows.map(([label, cell]) => (
            <div key={label} className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-1 sm:gap-3">
              <dt className="text-xs font-medium text-gray-500">{label}</dt>
              <dd
                className={`text-sm whitespace-pre-wrap break-words ${
                  !cell
                    ? 'text-gray-400'
                    : cell.tone === 'danger'
                      ? 'text-red-600 font-medium'
                      : cell.tone === 'archive'
                        ? 'text-gray-500'
                        : 'text-gray-800'
                }`}
              >
                {cell ? cell.text : '—'}
                {cell?.note && <span className="ml-1 text-xs text-gray-400">({cell.note})</span>}
              </dd>
            </div>
          ))}
          {check?.brokerDescription && (
            <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-1 sm:gap-3">
              <dt className="text-xs font-medium text-gray-500">Описание брокера</dt>
              <dd className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                {check.brokerDescription}
              </dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  )
}
